import { useQueryClient } from "@tanstack/react-query"
import { useChat } from "@ai-sdk/react"
import { omit } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { v4 } from "uuid"
import { getNeedSignTerms } from "modules/account-root/selectors"
import { useAccountAlias, useUserId } from "resources/account"
import { ChatTransport } from "../utils/chat-transport"
import { MANAGED_AGENT_TYPE } from "../helpers/constants"
import { RetryableError } from "../helpers/errors"
import { isToolPart } from "../helpers/tool-parts"
import { addMessagesToCache, createOptimisticMessage, getMessagesQueryKey, removeOptimisticMessages } from "../utils/message-cache"
import { addThreadToCache, getThreadsQueryKey } from "../utils/threads-cache"
import { useAiTeammateContext, useThreadsContext } from "./context"
import { buildSendMessagePayload } from "./send-message"

const EMPTY_PARTS = []

function getMessageText(message) {
  return message.parts
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("")
}

export function splitStreamingParts(parts) {
  const lastTextIndex = parts.findLastIndex((part) => part.type === "text")
  if (lastTextIndex === -1 || parts.slice(lastTextIndex + 1).some(isToolPart)) {
    return { stepParts: parts, liveText: "" }
  }
  const stepParts = lastTextIndex > 0 ? parts.slice(0, lastTextIndex) : EMPTY_PARTS
  return { stepParts, liveText: parts[lastTextIndex].text }
}

function findActiveSendEntry(activeSends, matchThreadId) {
  for (const [key, send] of activeSends) {
    if (send.threadId === matchThreadId) return [key, send]
  }
  return null
}

export default function useStreamingSendMessage() {
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const { modelId, setError, isManagedAgent } = useAiTeammateContext()
  const { openThread, threadId } = useThreadsContext()
  const needSignTerms = useSelector(getNeedSignTerms)
  const queryClient = useQueryClient()
  const [lastSendVariables, setLastSendVariables] = useState(null)
  const [finishErrored, setFinishErrored] = useState(false)
  const [finishedTurns, setFinishedTurns] = useState({ stepsByMessageId: {}, lastMessageId: null, lastMessageThreadId: null })
  const [pendingThreadIds, setPendingThreadIds] = useState(() => new Set())
  const activeSendsRef = useRef(new Map())
  const threadIdRef = useRef(threadId)
  const sendChatIdRef = useRef(null)
  const skipResumeForRef = useRef(null)
  const stopRequestedThreadIdRef = useRef(null)

  const needSignTermsRef = useRef(needSignTerms)
  useEffect(() => {
    needSignTermsRef.current = needSignTerms
  }, [needSignTerms])

  const transport = useMemo(() => {
    const chatTransport = new ChatTransport({ accountAlias, account: accountAlias, userId, getNeedSignTerms: () => needSignTermsRef.current })
    return {
      sendMessages: (options) => chatTransport.sendMessages({ ...options, chatId: sendChatIdRef.current ?? null }),
      reconnectToStream: async (options) => {
        const chatId = threadIdRef.current ?? null
        const stream = await chatTransport.reconnectToStream({ ...options, chatId })
        if (stream != null && chatId) {
          setPendingThreadIds((current) => (current.has(chatId) ? current : new Set(current).add(chatId)))
        }
        return stream
      },
      cancelStream: (chatId) => chatTransport.cancelStream(chatId),
    }
  }, [accountAlias, userId])

  function removePendingThread(chatId) {
    setPendingThreadIds((current) => {
      if (!current.has(chatId)) return current
      const next = new Set(current)
      next.delete(chatId)
      return next
    })
  }

  function clearActiveSend(found) {
    if (!found) return
    const [activeSendKey, activeSend] = found
    activeSendsRef.current.delete(activeSendKey)
    removePendingThread(activeSend.threadId)
    if (activeSend.messagesCacheSnapshot) {
      queryClient.setQueryData(getMessagesQueryKey(accountAlias, activeSend.threadId), activeSend.messagesCacheSnapshot)
    }
  }

  function clearPendingThread(chatId) {
    if (chatId == null) return
    removePendingThread(chatId)
  }

  function recordFinishedTurn(messageId, stepParts, finishedThreadId) {
    setFinishedTurns((current) => ({
      stepsByMessageId: stepParts.length > 0
        ? { ...current.stepsByMessageId, [messageId]: stepParts }
        : current.stepsByMessageId,
      lastMessageId: messageId,
      lastMessageThreadId: finishedThreadId,
    }))
  }

  function findFinishingSend(matchThreadId) {
    const byThread = findActiveSendEntry(activeSendsRef.current, matchThreadId) ?? findActiveSendEntry(activeSendsRef.current, null)
    if (byThread) return byThread
    if (matchThreadId == null && activeSendsRef.current.size === 1) return [...activeSendsRef.current][0]
    return null
  }

  function handleFinish({ message, isError, isAbort, finishReason }) {
    const newThreadId = message.metadata?.threadId
    if (isError) {
      const found = findFinishingSend(newThreadId)
      clearPendingThread(newThreadId)
      if (found) {
        clearActiveSend(found)
        setFinishErrored(true)
        setError(new RetryableError("Oops! Something went wrong on my end. Please try again."))
      }
      return
    }
    if (isAbort || finishReason === "abort") {
      const stopRequestedThreadId = stopRequestedThreadIdRef.current
      const stoppedThreadId = newThreadId ?? stopRequestedThreadId
      const found = newThreadId != null
        ? findFinishingSend(newThreadId)
        : findActiveSendEntry(activeSendsRef.current, stopRequestedThreadId)
      if (newThreadId == null || newThreadId === stopRequestedThreadId) stopRequestedThreadIdRef.current = null
      if (found) {
        const [activeSendKey, activeSend] = found
        activeSendsRef.current.delete(activeSendKey)
        removePendingThread(activeSend.threadId)
        const queryKey = getMessagesQueryKey(accountAlias, activeSend.threadId)
        removeOptimisticMessages(queryClient, queryKey)
        addMessagesToCache(queryClient, queryKey, [omit(activeSend.optimisticUserMessage, "isOptimistic")])
      } else {
        clearPendingThread(stoppedThreadId)
      }
      return
    }
    const found = findFinishingSend(newThreadId)
    if (!found) {
      if (newThreadId) {
        removePendingThread(newThreadId)
        const reattachedMessageId = message.metadata?.messageId
        const reattachedStepParts = reattachedMessageId ? splitStreamingParts(message.parts).stepParts : []
        if (reattachedMessageId) {
          recordFinishedTurn(reattachedMessageId, reattachedStepParts, newThreadId)
        }
        queryClient.removeQueries({ queryKey: getMessagesQueryKey(accountAlias, newThreadId), exact: true })
      }
      return
    }
    const [activeSendKey, activeSend] = found
    activeSendsRef.current.delete(activeSendKey)
    removePendingThread(activeSend.threadId)

    const { optimisticUserMessage } = activeSend
    const assistantMessage = {
      id: message.metadata?.messageId,
      role: "assistant",
      text: message.metadata?.text ?? getMessageText(message),
      createdAt: new Date().toISOString(),
      artifacts: message.metadata?.artifacts ?? [],
    }
    const realMessages = [omit(optimisticUserMessage, "isOptimistic"), assistantMessage]

    const originalQueryKey = getMessagesQueryKey(accountAlias, activeSend.threadId)
    removeOptimisticMessages(queryClient, originalQueryKey)

    if (!activeSend.threadId) {
      const threadsQueryKey = getThreadsQueryKey(accountAlias)
      addThreadToCache(queryClient, threadsQueryKey, {
        threadId: newThreadId,
        title: optimisticUserMessage.text,
        updatedAt: new Date().toISOString(),
        agentType: MANAGED_AGENT_TYPE,
      })
      skipResumeForRef.current = newThreadId
      openThread(newThreadId, optimisticUserMessage.text)
    }

    const newQueryKey = getMessagesQueryKey(accountAlias, activeSend.threadId || newThreadId)
    if (!assistantMessage.id) {
      queryClient.removeQueries({ queryKey: newQueryKey, exact: true })
      return
    }
    const completedStepParts = splitStreamingParts(message.parts).stepParts
    recordFinishedTurn(assistantMessage.id, completedStepParts, activeSend.threadId || newThreadId)
    addMessagesToCache(queryClient, newQueryKey, realMessages)
  }

  function handleError(error) {
    const chatId = error?.chatId ?? null
    const found = findFinishingSend(chatId)
    if (found) {
      clearActiveSend(found)
      setError(new RetryableError("Oops! Something went wrong on my end. Please try again."))
      return
    }
    clearPendingThread(chatId)
    if (chatId != null && chatId === threadIdRef.current) {
      queryClient.invalidateQueries({ queryKey: getMessagesQueryKey(accountAlias, chatId), exact: true })
    }
  }

  const { messages, status, sendMessage, resumeStream, stop } = useChat({
    id: threadId ?? undefined,
    transport,
    onFinish: handleFinish,
    onError: handleError,
  })

  const stopSend = useCallback(() => {
    stopRequestedThreadIdRef.current = threadId ?? null
    transport.cancelStream(threadId ?? null)
    stop()
  }, [threadId, transport, stop])

  useEffect(() => {
    if (!isManagedAgent || !threadId) return
    threadIdRef.current = threadId
    if (needSignTerms) return
    if (skipResumeForRef.current === threadId) {
      skipResumeForRef.current = null
      return
    }
    resumeStream()
  }, [isManagedAgent, needSignTerms, threadId, resumeStream])

  const [previousThreadId, setPreviousThreadId] = useState(threadId)
  if (threadId !== previousThreadId) {
    setPreviousThreadId(threadId)
    setFinishedTurns((current) => (
      current.lastMessageThreadId != null && current.lastMessageThreadId !== threadId
        ? { ...current, lastMessageId: null, lastMessageThreadId: null }
        : current
    ))
  }

  const stopRef = useRef(stop)
  useEffect(() => {
    stopRef.current = stop
  }, [stop])
  useEffect(() => () => {
    stopRequestedThreadIdRef.current = sendChatIdRef.current ?? null
    stopRef.current?.()
  }, [])

  const mutate = useCallback(async (variables) => {
    if (needSignTerms) return
    const { messageText, threadId: sendThreadId, lastAssistantMessageId } = variables
    if (findActiveSendEntry(activeSendsRef.current, sendThreadId)) return
    setLastSendVariables(variables)

    const queryKey = getMessagesQueryKey(accountAlias, sendThreadId)
    await queryClient.cancelQueries({ queryKey, exact: true })
    const messagesCacheSnapshot = queryClient.getQueryData(queryKey)

    const optimisticUserMessage = createOptimisticMessage(messageText, v4())
    addMessagesToCache(queryClient, queryKey, [optimisticUserMessage])
    setError(null)
    setFinishErrored(false)

    activeSendsRef.current.set(optimisticUserMessage.id, { threadId: sendThreadId, messagesCacheSnapshot, optimisticUserMessage })
    setPendingThreadIds((current) => new Set(current).add(sendThreadId))

    const payload = buildSendMessagePayload({
      messageText,
      isManagedAgent: true,
      agentContext: { modelId },
      threadId: sendThreadId,
      lastAssistantMessageId,
    })
    sendChatIdRef.current = sendThreadId
    sendMessage({ text: messageText }, { body: payload })
  }, [accountAlias, modelId, needSignTerms, queryClient, setError, sendMessage])

  const isPending = (status === "submitted" || status === "streaming") && pendingThreadIds.has(threadId)
  const lastMessage = messages[messages.length - 1]
  const { stepParts, liveText } = isPending && lastMessage?.role === "assistant"
    ? splitStreamingParts(lastMessage.parts)
    : { stepParts: EMPTY_PARTS, liveText: "" }
  const finishedTurnMessageId = finishedTurns.lastMessageThreadId === threadId ? finishedTurns.lastMessageId : null

  return {
    mutate,
    stop: stopSend,
    isPending,
    isError: status === "error" || finishErrored,
    variables: lastSendVariables,
    streamingParts: stepParts,
    streamingText: liveText,
    stepsByMessageId: finishedTurns.stepsByMessageId,
    finishedTurnMessageId,
  }
}
